<template>
  <div class="ap-page">
    <section class="ap-hero">
      <div class="ap-hero-copy">
        <span class="ap-kicker">Application timeline</span>
        <h1 class="ap-hero-title">Every step, <em>on record.</em></h1>
        <p class="ap-hero-sub">Who the application was sent to, when, and the remarks recorded at each desk.</p>
      </div>
      <div class="ap-hero-actions">
        <button type="button" class="ap-btn ap-btn--glass" @click="goBack">
          <v-icon small>mdi-arrow-left</v-icon>
          Back
        </button>
      </div>
    </section>

    <section class="ap-card">
      <div class="ap-card-body tl-body">
        <div v-if="loading"><v-skeleton-loader type="list-item-avatar-three-line@4"></v-skeleton-loader></div>

        <div v-else-if="!list.length" class="ap-empty">
          <span class="ap-empty-icon"><v-icon size="34">mdi-timeline-outline</v-icon></span>
          <div class="ap-empty-title">No activity yet</div>
          <div>Actions on this application will appear here.</div>
        </div>

        <ol v-else class="tl">
          <li v-for="(item, i) in list" :key="item._id || i" class="tl-item" :class="{ 'tl-item--last': i === list.length - 1 }">
            <div class="tl-when">
              <div class="tl-date">{{ formatDate(item.forwardDate || item.created_at) }}</div>
              <div class="tl-time">{{ formatTime(item.forwardDate || item.created_at) }}</div>
            </div>
            <span class="tl-dot"><v-icon x-small>{{ iconFor(item) }}</v-icon></span>
            <div class="tl-card">
              <div class="tl-title">{{ item.processDone || "Updated" }}</div>
              <div class="tl-route">
                <!-- One chain: the sender only when it isn't the previous
                     step's desk, so no officer is listed twice. -->
                <span v-if="showSender(i)" class="tl-person">
                  <span class="tl-person-name">{{ item.forwardFrom.username || item.forwardFrom.officeName }}</span>
                  <span class="tl-person-role">{{ roleOf(item.forwardFrom) }}</span>
                </span>
                <v-icon v-if="showSender(i) && item.forwardTo" small class="tl-arrow">mdi-arrow-right</v-icon>
                <!-- Sent to a desk: any officer in that role can take it up, and
                     their name shows on the step where they act. -->
                <span v-if="item.forwardTo" class="tl-person tl-person--desk">
                  <span class="tl-person-name">{{ deskName(item.forwardTo) }}</span>
                  <span class="tl-person-role">
                    <template v-if="String(item.forwardTo.uType) === '772'">Applicant</template>
                    <template v-else-if="handledBy(i)">Handled by {{ handledBy(i).username || handledBy(i).officeName }}</template>
                    <template v-else-if="i === list.length - 1">Waiting here now</template>
                    <template v-else>Desk</template>
                  </span>
                </span>
              </div>
              <div v-if="item.remarks" class="tl-remarks">“{{ item.remarks }}”</div>
            </div>
          </li>
        </ol>
      </div>
    </section>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";

const ROLE_LABELS = {
  780: "Super Admin",
  778: "Government",
  777: "Admin",
  775: "Circle",
  774: "DFO",
  771: "DFO",
  7741: "Section Clerk",
  7742: "Superintendent",
  773: "Range Officer",
  772: "Applicant",
};

// Full forwarding history of one application (/records?id=…).
export default {
  name: "RecordsPage",
  data() {
    return {
      id: this.$route.query.id,
      list: [],
      loading: true,
    };
  },
  mounted() {
    this.getList();
  },
  methods: {
    goBack() {
      const sourceComponent = this.$route.query.from;
      // Map of component names to their routes
      const routeMap = {
        CampDetailedViewClerk: "/CampDetailedViewClerk",
        cancelview: "/FilmDetailedView",
        FilmDetailedView: "/FilmDetailedView",
        AdminFilmDetailedView: "/AdminFilmDetailedView",
        CampDetviewsuperint: "/DetailedViewsuperintedent",
        CampDetailedView: "/CampDetailedView",
        CancelledAppView: "/CancelledApplicationView",
      };
      const targetRoute = routeMap[sourceComponent];
      // Opened from somewhere else (e.g. the timeline dialog): just go back.
      if (!targetRoute) {
        this.$router.go(-1);
        return;
      }
      this.$router.push({ path: targetRoute, query: { id: this.$route.query.id } });
    },
    roleOf(user) {
      return (user && ROLE_LABELS[Number(user.uType)]) || (user && user.officeName) || "";
    },
    // Same desk: same role (both DFO codes count as one); applicants by person.
    sameDesk(a, b) {
      if (!a || !b) return false;
      const norm = (u) => (String(u.uType) === "771" ? "774" : String(u.uType));
      if (norm(a) !== norm(b)) return false;
      return norm(a) !== "772" || String(a._id) === String(b._id);
    },
    showSender(i) {
      const item = this.list[i];
      if (!item.forwardFrom) return false;
      return i === 0 || !this.sameDesk(this.list[i - 1].forwardTo, item.forwardFrom);
    },
    // Who at the destination desk acted on it: the sender of the next step.
    handledBy(i) {
      const next = this.list[i + 1];
      if (!next || !next.forwardFrom) return null;
      return this.sameDesk(this.list[i].forwardTo, next.forwardFrom) ? next.forwardFrom : null;
    },
    // Recipient of a step: the applicant by name, otherwise the role's desk.
    deskName(user) {
      if (user && String(user.uType) === "772") return user.username || "Applicant";
      const role = ROLE_LABELS[Number(user && user.uType)];
      return role ? role + " desk" : (user && (user.username || user.officeName)) || "";
    },
    iconFor(item) {
      const text = (item.processDone || "").toLowerCase();
      if (text.includes("reject")) return "mdi-close";
      if (text.includes("cancel")) return "mdi-cancel";
      if (text.includes("payment") || text.includes("accepted")) return "mdi-cash-check";
      if (text.includes("range")) return "mdi-map-marker-outline";
      if (text.includes("submi")) return "mdi-send-check-outline";
      return "mdi-arrow-right";
    },
    formatDate(value) {
      return value ? moment(value).format("D MMM YYYY") : "—";
    },
    formatTime(value) {
      return value ? moment(value).format("h:mm A") : "";
    },
    getList() {
      this.loading = true;
      axios({
        method: "POST",
        url: "/records/application",
        headers: { token: localStorage.getItem("token") },
        data: { appid: this.id },
      })
        .then((response) => {
          this.loading = false;
          this.list = (response.data && response.data.data) || [];
        })
        .catch((err) => {
          this.loading = false;
          console.log(err);
        });
    },
  },
};
</script>

<style scoped>
.tl-body {
  padding-top: 24px;
}

.tl {
  list-style: none;
  margin: 0;
  padding: 0;
}

.tl-item {
  position: relative;
  display: grid;
  grid-template-columns: 120px 30px minmax(0, 1fr);
  gap: 0 16px;
  padding-bottom: 22px;
}

.tl-item::before {
  content: "";
  position: absolute;
  top: 30px;
  bottom: 0;
  left: calc(120px + 16px + 14px);
  width: 2px;
  background: linear-gradient(180deg, rgba(22, 163, 74, 0.45), rgba(184, 149, 90, 0.35));
}

.tl-item--last::before {
  display: none;
}

.tl-when {
  padding-top: 6px;
  text-align: right;
}

.tl-date {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--ap-ink);
}

.tl-time {
  font-size: 12px;
  color: var(--ap-muted);
}

.tl-dot {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  margin-top: 2px;
  border-radius: 50%;
  background: var(--ap-forest);
  box-shadow: 0 0 0 5px #ecfdf3;
}

.tl-dot .v-icon {
  color: var(--ap-gold-soft) !important;
}

.tl-card {
  padding: 14px 18px;
  border-radius: 16px;
  border: 1px solid var(--ap-line);
  background: linear-gradient(180deg, #fbfdfc 0%, #ffffff 100%);
}

.tl-title {
  font-family: "Inter", sans-serif;
  font-size: 18px;
  font-weight: 500;
  color: var(--ap-ink);
}

.tl-route {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}

.tl-person {
  display: inline-flex;
  flex-direction: column;
  padding: 6px 12px;
  border-radius: 12px;
  background: #f4f8f5;
}

.tl-person--desk {
  background: #fdf8ee;
}

.tl-person-name {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--ap-ink);
}

.tl-person-role {
  font-size: 11.5px;
  letter-spacing: 0.4px;
  color: var(--ap-muted);
}

.tl-arrow {
  color: var(--ap-gold) !important;
}

.tl-remarks {
  margin-top: 10px;
  padding-left: 12px;
  border-left: 2px solid var(--ap-gold-soft);
  font-size: 14px;
  font-style: italic;
  color: #3d4a43;
}

@media (max-width: 600px) {
  .tl-item {
    grid-template-columns: 30px minmax(0, 1fr);
  }

  .tl-item::before {
    left: 14px;
  }

  .tl-when {
    grid-column: 2;
    grid-row: 1;
    padding: 0 0 6px;
    text-align: left;
  }

  .tl-dot {
    grid-column: 1;
    grid-row: 1 / span 2;
  }

  .tl-card {
    grid-column: 2;
  }
}
</style>
